import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight, Crown, Truck } from "lucide-react";
import { useAuth, useCart } from "../contexts";
import { api, resolveFileUrl } from "../api";
import { PageHeader, EmptyState } from "../components/ui-bits";

export default function CartPage() {
  const { items, removeItem, setQty, total, clear } = useCart();
  const { culturePassActive } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  // Fetch a live shipping estimate every time the cart changes so the buyer
  // sees the exact number Stripe will charge — kept identical to the server-side
  // math in POST /api/payments/checkout via the shared `compute_shipping()`.
  const refresh = useCallback(async () => {
    if (items.length === 0) { setQuote(null); return; }
    setQuoting(true);
    try {
      const body = { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size || "" })) };
      const { data } = await api.post("/shipping/quote", body);
      setQuote(data);
    } catch { setQuote(null); }
    finally { setQuoting(false); }
  }, [items]);
  useEffect(() => { refresh(); }, [refresh]);

  const subtotal = quote?.subtotal ?? total;
  const discount = quote?.discount ?? (culturePassActive ? +(total * 0.10).toFixed(2) : 0);
  const shipping = quote?.shipping ?? null;
  const tax = quote?.tax ?? 0;
  const taxRatePct = quote?.tax_rate_pct ?? 0;
  const taxLabel = quote?.tax_label || "SALES TAX";
  const grand = quote?.total ?? +(total - discount).toFixed(2);
  const freeApplied = quote?.free_shipping_applied;
  const threshold = quote?.shipping_free_threshold ?? 0;
  const remainingForFree = threshold > 0 ? Math.max(0, threshold - (subtotal - discount)) : 0;

  return (
    <div className="pb-32">
      <PageHeader kicker="THE BAG" title="YOUR CART" sub="Review items, adjust quantities, and proceed to secure checkout." />
      <div className="divider-tape" />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10">
        {items.length === 0 ? (
          <div className="space-y-6">
            <EmptyState>Your cart is empty.</EmptyState>
            <Link to="/merch" className="btn-primary" data-testid="cart-empty-shop-link">Shop Merch <ArrowRight size={16} /></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_360px] gap-10">
            <div className="border border-neutral-800" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.key} className="flex items-center gap-4 p-4 border-b border-neutral-800 last:border-0" data-testid={`cart-item-${i.product_id}`}>
                  <img src={resolveFileUrl(i.image)} alt={i.title} className="w-20 h-20 object-cover border border-neutral-800" />
                  <div className="flex-1 min-w-0">
                    <div className="font-headline text-base uppercase truncate">{i.title}</div>
                    <div className="font-mono-label text-[10px] text-neutral-400 mt-1">
                      {i.category?.toUpperCase()}{i.size && ` · SIZE ${i.size}`}
                    </div>
                    <div className="font-display text-lg mt-1">${(i.price * i.quantity).toFixed(2)}</div>
                  </div>
                  <div className="inline-flex items-center border border-neutral-800">
                    <button onClick={() => setQty(i.key, i.quantity - 1)} className="w-8 h-8" data-testid={`cart-minus-${i.product_id}`}><Minus size={12} className="mx-auto" /></button>
                    <span className="w-8 text-center font-mono-label text-sm">{i.quantity}</span>
                    <button onClick={() => setQty(i.key, i.quantity + 1)} className="w-8 h-8" data-testid={`cart-plus-${i.product_id}`}><Plus size={12} className="mx-auto" /></button>
                  </div>
                  <button onClick={() => removeItem(i.key)} className="text-neutral-500 hover:text-[var(--accent)] p-2" data-testid={`cart-remove-${i.product_id}`} aria-label="Remove">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <aside className="border border-neutral-800 p-6 self-start" data-testid="cart-summary">
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-3">ORDER SUMMARY</div>
              <div className="flex justify-between mb-2 font-mono-label text-xs text-neutral-300">
                <span>SUBTOTAL</span>
                <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between mb-2 font-mono-label text-xs text-[var(--accent)]" data-testid="cart-pass-discount">
                  <span className="inline-flex items-center gap-1"><Crown size={12} /> CULTURE PASS · 10%</span>
                  <span>−${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between mb-2 font-mono-label text-xs text-neutral-300" data-testid="cart-shipping-row">
                <span className="inline-flex items-center gap-1"><Truck size={12} /> SHIPPING</span>
                {quoting ? (
                  <span className="text-neutral-500">CALCULATING…</span>
                ) : freeApplied ? (
                  <span className="text-[var(--accent)]" data-testid="cart-shipping-free">FREE</span>
                ) : shipping !== null ? (
                  <span data-testid="cart-shipping-amount">${shipping.toFixed(2)}</span>
                ) : (
                  <span className="text-neutral-500">CALC. AT CHECKOUT</span>
                )}
              </div>
              {!freeApplied && remainingForFree > 0 && (
                <div className="font-mono-label text-[10px] text-neutral-500 mb-3" data-testid="cart-free-shipping-nudge">
                  ADD ${remainingForFree.toFixed(2)} MORE FOR FREE SHIPPING
                </div>
              )}
              {tax > 0 && (
                <div className="flex justify-between mb-2 font-mono-label text-xs text-neutral-300" data-testid="cart-tax-row">
                  <span>{taxLabel} · {taxRatePct.toFixed(taxRatePct % 1 ? 2 : 0)}%</span>
                  <span data-testid="cart-tax-amount">${tax.toFixed(2)}</span>
                </div>
              )}
              <div className="divider-tape mb-4" />
              <div className="flex justify-between mb-3">
                <span className="font-headline uppercase">Total</span>
                <span className="font-display text-2xl" data-testid="cart-total">${grand.toFixed(2)}</span>
              </div>
              {!culturePassActive && (
                <Link to="/culture-pass" className="block mb-4 text-[10px] font-mono-label text-neutral-400 hover:text-[var(--accent)]" data-testid="cart-pass-upsell">
                  GET CULTURE PASS · SAVE 10% ON EVERY ORDER →
                </Link>
              )}
              <button onClick={() => navigate("/checkout")} className="btn-primary w-full justify-center mb-3" data-testid="cart-checkout-btn">
                Secure Checkout <ArrowRight size={16} />
              </button>
              <button onClick={clear} className="btn-ghost w-full justify-center" data-testid="cart-clear-btn">Clear Cart</button>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
